/**
 * screens/admin/adminOrganizations.tsx — customer organizations (tenants).
 * Faithful RN port of design/admin.jsx OrganizationsPage: search + status filter
 * chips + suspend/reactivate. Reads live tenants via platformApi.adminListOrgs
 * (falls back to seeded tenants offline).
 */

import React, { useEffect, useState } from 'react';
import { View, Text, ScrollView, Pressable } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useToast } from '../../components/toast';
import { SettingsHeader, Panel, DataTable, Tag, StatusBadge } from '../../components/platform/primitives';
import { PButton, PTextInput, PModal } from '../../components/platform/controls';
import { ZoneSelect, ZoneConfirmBody, FALLBACK_ZONES } from '../../components/platform/zoneSelect';
import { PermissionDenied } from '../shell/chrome';
import { FS, FW, R, inr } from '../../utils/platformTokens';
import { adminListOrgs } from '../../services/platform/platformApi';

type Tenant = typeof import('../../data/platformSeed').SEED.tenants[number];

const STATUS_LABEL: Record<string, string> = { active: 'Active', trial: 'Trial', past_due: 'Past due', suspended: 'Suspended' };
const FILTERS = ['all', 'active', 'trial', 'past_due', 'suspended'];

const AdminOrganizations: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { state, setState, isPlatformAdmin } = usePlatform();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  /**
   * SKPLATFORM-UICC-006 C-3 — the zone filter.
   *
   * This is the screen where "which of our tenants are in the UAE" gets
   * answered, and it will be asked during any residency review. The list keeps
   * its GLOBAL reach: Platform stays global because licensing and billing are
   * global, so zone adds a dimension here rather than sharding the screen.
   */
  const [zoneFilter, setZoneFilter] = useState<string>('all');

  /* C-2 — provisioning. Zone is chosen once, here, and never again. */
  const [provisioning, setProvisioning] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  /** null means NOTHING chosen — not a stand-in for a default. */
  const [newOrgZone, setNewOrgZone] = useState<string | null>(null);
  const [confirmingZone, setConfirmingZone] = useState(false);

  useEffect(() => { adminListOrgs().catch(() => undefined); }, []);
  if (!isPlatformAdmin) return <PermissionDenied />;

  const list = state.tenants.filter(
    (t) =>
      (filter === 'all' || t.status === filter) &&
      (zoneFilter === 'all' || (t as { zone?: string }).zone === zoneFilter) &&
      t.name.toLowerCase().includes(q.toLowerCase()),
  );
  const suspend = (t: Tenant) => {
    setState((s) => ({ ...s, tenants: s.tenants.map((x) => (x.id === t.id ? { ...x, status: x.status === 'suspended' ? 'active' : 'suspended', mrr: x.status === 'suspended' ? (x.plan === 'Starter' ? 0 : x.mrr) : 0 } : x)) }));
    toast.success(t.status === 'suspended' ? `${t.name} reactivated.` : `${t.name} suspended.`);
  };

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1100, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader
        title="Organizations"
        sub="Every customer workspace on the platform."
        action={
          <PButton kind="accent" onPress={() => setProvisioning(true)}>
            New organisation
          </PButton>
        }
      />

      <Panel>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <View style={{ flex: 1, minWidth: 200 }}>
            <PTextInput leadingIcon="search" placeholder="Search organizations…" value={q} onChangeText={setQ} />
          </View>
          <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
            {FILTERS.map((f) => (
              <Pressable key={f} onPress={() => setFilter(f)}>
                <Tag tone={filter === f ? 'accent' : 'neutral'} size="lg">{f === 'all' ? 'All' : STATUS_LABEL[f]}</Tag>
              </Pressable>
            ))}
          </View>
          {/* C-3 — zone filter. Codes only, in mono, because a filter is
              scanned rather than read. */}
          <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <Text style={{ fontSize: FS.xs, color: c.ink4 }}>Zone</Text>
            {['all', ...FALLBACK_ZONES.map((z) => z.code)].map((z) => (
              <Pressable key={z} onPress={() => setZoneFilter(z)}>
                <Tag tone={zoneFilter === z ? 'accent' : 'neutral'} size="lg">
                  {z === 'all' ? 'All' : z}
                </Tag>
              </Pressable>
            ))}
          </View>
        </View>
      </Panel>

      <Panel bodyStyle={{ padding: 0 }}>
        <DataTable<Tenant>
          minWidth={880}
          keyExtractor={(t) => t.id}
          rows={list}
          empty={<Text style={{ color: c.ink3 }}>No organizations match.</Text>}
          columns={[
            {
              key: 'name', header: 'Organization', width: 240,
              render: (t) => (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <View style={{ width: 28, height: 28, borderRadius: R.r2, backgroundColor: c.ink, alignItems: 'center', justifyContent: 'center' }}>
                    <Text style={{ color: c.paper, fontWeight: '700', fontSize: 12 }}>{t.name[0]}</Text>
                  </View>
                  <Text style={{ fontWeight: FW.medium, color: c.ink }}>{t.name}</Text>
                </View>
              ),
            },
            { key: 'plan', header: 'Plan', width: 110, render: (t) => <Tag tone="neutral">{t.plan}</Tag> },
            {
              /**
               * C-3 — Zone. THE CODE ONLY, in mono: the full name lives on the
               * detail screen, and a list needs scannability.
               *
               * Note what is deliberately NOT added to this table: a customer
               * count. This row already carries `Seats`, and DB-006 §2 rule 1
               * forbids a seat count and a customer count in the same numeric
               * group — an operator reading them side by side will add them up.
               * Customer activity has its own screen (N-6).
               */
              key: 'zone', header: 'Zone', width: 80,
              render: (t) => (
                <Text style={{ fontFamily: theme.font.mono, color: c.ink2, fontSize: FS.md }}>
                  {(t as { zone?: string }).zone ?? '—'}
                </Text>
              ),
            },
            { key: 'seats', header: 'Seats', width: 70, render: (t) => <Text style={{ fontFamily: theme.font.mono, color: c.ink }}>{t.seats}</Text> },
            { key: 'mrr', header: 'MRR', width: 100, render: (t) => <Text style={{ fontFamily: theme.font.mono, color: c.ink }}>{t.mrr === 0 ? '—' : inr(t.mrr)}</Text> },
            { key: 'signup', header: 'Signed up', width: 120, render: (t) => <Text style={{ color: c.ink3, fontSize: FS.md }}>{t.signup}</Text> },
            { key: 'status', header: 'Status', width: 110, render: (t) => <StatusBadge status={t.status} label={STATUS_LABEL[t.status]} /> },
            {
              key: 'actions', header: 'Actions', align: 'right',
              render: (t) => (
                <PButton size="sm" kind="ghost" textColor={t.status !== 'suspended' ? c.danger : undefined} onPress={() => suspend(t)}>
                  {t.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                </PButton>
              ),
            },
          ]}
        />
      </Panel>

      {/*
        C-2 — provisioning. Zone is the only field here with a confirmation
        step, and the form will not submit without one.
      */}
      <PModal
        open={provisioning}
        onClose={() => { setProvisioning(false); setConfirmingZone(false); }}
        title="New organisation"
        footer={
          <View style={{ flexDirection: 'row', gap: 10, justifyContent: 'flex-end' }}>
            <PButton kind="ghost" onPress={() => { setProvisioning(false); setConfirmingZone(false); }}>
              Cancel
            </PButton>
            {!confirmingZone ? (
              <PButton
                kind="accent"
                /* No default zone means no submit until one is chosen. */
                disabled={!newOrgName.trim() || !newOrgZone}
                onPress={() => setConfirmingZone(true)}
              >
                Continue
              </PButton>
            ) : (
              <PButton
                kind="accent"
                onPress={() => {
                  toast.success(`Organisation queued for creation in zone ${newOrgZone}.`);
                  setProvisioning(false);
                  setConfirmingZone(false);
                  setNewOrgName('');
                  setNewOrgZone(null);
                }}
              >
                {/* The button NAMES the zone — "Confirm" can be clicked
                    without reading; this cannot. */}
                Create in {newOrgZone}
              </PButton>
            )}
          </View>
        }
      >
        {!confirmingZone ? (
          <View style={{ gap: 16 }}>
            <View>
              <Text style={{ fontSize: FS.md, color: c.ink2, fontWeight: FW.medium, marginBottom: 6 }}>
                Organisation name
              </Text>
              <PTextInput value={newOrgName} onChangeText={setNewOrgName} placeholder="Acme Retail Pvt Ltd" />
            </View>
            <ZoneSelect value={newOrgZone} onChange={setNewOrgZone} />
          </View>
        ) : (
          <ZoneConfirmBody zone={FALLBACK_ZONES.find((z) => z.code === newOrgZone)!} />
        )}
      </PModal>
    </ScrollView>
  );
};

export default AdminOrganizations;
